import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { ListeChoix } from "./ListeChoix";
import { FenetreConfirmation } from "./FenetreConfirmation";
import {
  Loader2,
  Send,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import {
  sendContactEmail,
  validateContactData,
} from "../services/emailService";

const coordinates = [
  {
    icon: MapPin,
    label: "Adresse",
    value: ["30 rue des Prés", "Gatineau, QC J9A 3G8"],
  },
  {
    icon: Mail,
    label: "Courriel",
    value: ["info@geodonnee.com"],
    href: "mailto:info@geodonnee.com",
  },
  {
    icon: Clock,
    label: "Heures",
    value: ["Lundi au vendredi", "9h00 – 17h00 (HNE)"],
  },
];

const projectTypes = [
  { value: "traitement", label: "Traitement de données" },
  { value: "automatisation", label: "Automatisation SIG" },
  { value: "diffusion", label: "Diffusion / visualisation" },
  { value: "developpement", label: "Cartes web / développement sur mesure" },
  { value: "sous-traitance", label: "Sous-traitance : saisie ou collecte" },
  { value: "formation", label: "Formation" },
  { value: "consultation", label: "Consultation" },
  { value: "autre", label: "Autre projet" },
];

const nextSteps = [
  "Nous lisons votre demande et revenons vers vous sous 24 h ouvrables.",
  "Un appel de 30 minutes pour cadrer le besoin et vos contraintes.",
  "Une proposition chiffrée, avec le périmètre et les livrables.",
];

const emptyForm = {
  name: "",
  email: "",
  company: "",
  subject: "",
  message: "",
};

const fieldOrder = ["name", "email", "company", "subject", "message"];

const fieldId = (field) => `contact-${field}`;
const errorId = (field) => `contact-${field}-erreur`;

const Field = ({ field, label, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={fieldId(field)} id={`${fieldId(field)}-libelle`}>
      {label} <span aria-hidden="true">*</span>
    </Label>
    {children}
    {error && (
      <p
        id={errorId(field)}
        className="text-vt-controle font-semibold text-destructive"
      >
        {error}
      </p>
    )}
  </div>
);

export const Contact = () => {
  const [contactForm, setContactForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  // Confirmation d'envoi affichée dans une fenêtre (nom et courriel gardés
  // avant de vider le formulaire).
  const [confirmation, setConfirmation] = useState(null);
  const boutonEnvoi = useRef(null);
  const fermerConfirmation = useCallback(() => setConfirmation(null), []);
  const { search } = useLocation();

  // Type de projet présélectionné depuis une page de service ou de formation.
  useEffect(() => {
    const sujet = new URLSearchParams(search).get("sujet");
    if (projectTypes.some((type) => type.value === sujet)) {
      setContactForm((prev) => ({ ...prev, subject: sujet }));
    }
  }, [search]);

  const handleInputChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Nom, état d'erreur et message relié, communs à tous les champs.
  const a11y = (field) => ({
    id: fieldId(field),
    "aria-required": true,
    "aria-invalid": errors[field] ? true : undefined,
    "aria-describedby": errors[field] ? errorId(field) : undefined,
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const validationErrors = validateContactData(contactForm);
    const firstInvalid = fieldOrder.find((field) => validationErrors[field]);
    if (firstInvalid) {
      setErrors(validationErrors);
      setStatus({
        type: "error",
        message:
          "Le formulaire contient des erreurs : corrigez les champs indiqués.",
      });
      document.getElementById(fieldId(firstInvalid))?.focus();
      return;
    }

    setLoading(true);
    setStatus(null);

    const result = await sendContactEmail(contactForm);

    if (result.success) {
      setConfirmation({ nom: contactForm.name.trim(), courriel: contactForm.email.trim() });
      setContactForm(emptyForm);
      setErrors({});
    } else {
      setStatus({ type: "error", message: result.message });
    }

    setLoading(false);
  };

  return (
    <section id="contact" className="section section-alt">
      {confirmation && (
        <FenetreConfirmation
          {...confirmation}
          onFermer={fermerConfirmation}
          retourFocus={boutonEnvoi}
        />
      )}
      <div className="shell">
        <div className="mx-auto max-w-[840px] text-center">
          <h2 className="h2">
            Décrivez votre projet, <em>réponse sous 24 h</em>
          </h2>
          <p className="lede mt-6">
            Un premier échange sans frais pour cadrer le besoin et estimer
            l&apos;effort.
          </p>
        </div>

        <div className="mt-10 lg:mt-12 grid gap-6 lg:grid-cols-5">
          {/* Coordonnées */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            <div className="rounded-[20px] border border-border bg-white p-8">
              <h3 className="text-vt-h3 font-bold text-brand">
                Bureau de Gatineau
              </h3>
              <dl className="mt-6 space-y-6">
                {coordinates.map(({ icon: Icon, label, value, href }) => (
                  <div key={label} className="relative pl-10">
                    <dt className="text-vt-legende font-extrabold uppercase tracking-[0.12em] text-t3">
                      <Icon
                        className="absolute left-0 top-0.5 h-6 w-6 text-accent"
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                      {label}
                    </dt>
                    <dd className="mt-1 font-semibold leading-relaxed text-brand">
                      {value.map((line) => (
                        <span key={line} className="block">
                          {href ? <a href={href}>{line}</a> : line}
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-[20px] bg-brand p-8 text-white">
              <h3 className="text-vt-h3 font-bold">Ce qui se passe ensuite</h3>
              <ol className="mt-5 list-decimal space-y-4 pl-5 font-semibold leading-relaxed text-white/80 marker:font-extrabold marker:text-white">
                {nextSteps.map((step) => (
                  <li key={step} className="pl-1.5">
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Formulaire */}
          <div className="lg:col-span-3">
            <form
              noValidate
              onSubmit={handleContactSubmit}
              className="space-y-6 rounded-[20px] border border-border bg-white p-8 lg:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <Field field="name" label="Nom complet" error={errors.name}>
                  <Input
                    {...a11y("name")}
                    autoComplete="name"
                    value={contactForm.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    disabled={loading}
                  />
                </Field>

                <Field
                  field="email"
                  label="Courriel professionnel"
                  error={errors.email}
                >
                  <Input
                    {...a11y("email")}
                    type="email"
                    autoComplete="email"
                    value={contactForm.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    disabled={loading}
                  />
                </Field>

                <Field
                  field="company"
                  label="Organisation"
                  error={errors.company}
                >
                  <Input
                    {...a11y("company")}
                    autoComplete="organization"
                    value={contactForm.company}
                    onChange={(e) =>
                      handleInputChange("company", e.target.value)
                    }
                    disabled={loading}
                  />
                </Field>

                <Field
                  field="subject"
                  label="Type de projet"
                  error={errors.subject}
                >
                  <ListeChoix
                    {...a11y("subject")}
                    options={projectTypes}
                    valeur={contactForm.subject}
                    onChange={(value) => handleInputChange("subject", value)}
                    disabled={loading}
                  />
                </Field>
              </div>

              <Field
                field="message"
                label="Description du projet"
                error={errors.message}
              >
                <Textarea
                  {...a11y("message")}
                  rows={6}
                  className="resize-none"
                  value={contactForm.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  disabled={loading}
                />
              </Field>

              <div className="space-y-3">
                <button
                  ref={boutonEnvoi}
                  type="submit"
                  aria-busy={loading}
                  className="btn-accent w-full"
                >
                  {loading ? (
                    <>
                      <Loader2
                        className="h-5 w-5 animate-spin"
                        strokeWidth={2.4}
                        aria-hidden="true"
                      />
                      Envoi en cours
                    </>
                  ) : (
                    <>
                      Envoyer la demande
                      <Send
                        className="h-5 w-5"
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </button>

                {/* Erreur d'envoi, collée au bouton ; zone toujours présente. */}
                <div role="alert">
                  {status?.type === "error" && (
                    <p className="font-bold text-destructive">
                      {status.message}
                    </p>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
